import { Button, Stack, Table } from 'react-bootstrap';
import { TbMessageCircle2 } from 'react-icons/tb';
import { FcCheckmark } from 'react-icons/fc';
import { FaRegClock } from 'react-icons/fa';
import { BiEdit } from 'react-icons/bi';
import { format } from 'date-fns';
import { getFormattedShiftTimes } from '@services/shift/shiftService';
import { useActivityContext } from '@company/state/activityContext';
import { useChatContext } from '@company/state/chatContext';
import { useJobContext } from '@company/state/jobContext';
import { JobAssignment, ShiftPosition } from '@th-types/job.detail.type';
import { ActivityDetail } from '@th-types/daily-activity.type';
import { ActivityTrackerView } from '@th-types/jobs.type';
import DATE_FORMATS from '@constants/dateFormat';
import { useJobAssignmentStatus } from '@hooks/useJobAssignmentStatus';
import { convertToTimezone, getDateNowByTimezone } from '@utils/DateUtils';
import { isShiftPayable } from '@utils/ShiftsUtils';
import {
  getCheckInValue,
  getCheckOutValue,
  getStatusColumn,
} from '@utils/JobUtils';
import './styles.scss';

interface Props {
  viewType: string;
  groupedBy: string;
  activities: ActivityDetail[];
  handleConfirmMarkPaid: (
    groupedBy: string,
    id: number,
    filteredActivities: ActivityDetail[],
    all?: boolean
  ) => void;
  handleConfirmMarkUnPaid: (
    groupedBy: string,
    id: number,
    filteredActivities: ActivityDetail[],
    all?: boolean
  ) => void;
  openWorkerSideModal: (jobAssignment: JobAssignment) => void;
  handleOpenReviewShiftModal: (activity: ActivityDetail) => void;
  handleOpenSubmitHoursModal: (activity: ActivityDetail) => void;
  noticedMissingActivitiesRefreshQueryData: (shiftPositionId: number) => void;
  noticedMissingHoursRefreshQueryData: (shiftPositionId: number) => void;
}

export default function ActivityTrackerDesktopTable({
  activities,
  groupedBy,
  viewType,
  openWorkerSideModal,
  handleConfirmMarkPaid,
  handleConfirmMarkUnPaid,
  handleOpenSubmitHoursModal,
  handleOpenReviewShiftModal,
  noticedMissingHoursRefreshQueryData,
  noticedMissingActivitiesRefreshQueryData,
}: Props) {
  const { job } = useJobContext();
  const { completeOnboardingAction } = useActivityContext();
  const { openChat } = useChatContext();
  const jobAssignmentStatus = useJobAssignmentStatus()(undefined);
  const { shouldShowWorkerInfoInChat } = jobAssignmentStatus;
  const TABLE_HEADER_WEB = [
    'WORKER',
    'SHIFT STATUS',
    'ONBOARDING',
    'SCHEDULED TIME',
    'CHECK IN',
    'CHECK OUT',
    'SUBMITTED TIME',
    'TOTAL HOURS',
    'WORK PAY',
    'TOTAL PAY',
    'SHIFT PAID',
  ];
  if (viewType === ActivityTrackerView.WORKER) {
    TABLE_HEADER_WEB.shift();
    TABLE_HEADER_WEB.unshift('DATE');
  }

  if (!job) {
    return null;
  }

  const hasShiftStarted = (shift: ShiftPosition) => {
    return (
      convertToTimezone(new Date(shift.start), job.address.timezoneId) <
      getDateNowByTimezone(job.address.timezoneId)
    );
  };

  return (
    <Table responsive borderless>
      <thead className="table-header fw-bold">
        <tr>
          {TABLE_HEADER_WEB.map((header, index) => (
            <th className={index === 0 ? 'text-start' : ''} key={header}>
              {header}
            </th>
          ))}
          <th aria-label="edit" />
          <th aria-label="shift-review" />
        </tr>
      </thead>
      <tbody className="at-table-body">
        {activities.map((activity) => (
          <tr key={activity.shiftPosition.id}>
            <td className="text-start text-truncate" style={{ width: '180px' }}>
              {viewType === ActivityTrackerView.DATE ? (
                <span
                  className="text-capitalize cursor-point at-name-link"
                  onClick={() =>
                    openWorkerSideModal(activity.shiftPosition.jobAssignment!)
                  }
                >
                  {activity.shiftPosition.jobAssignment!.worker.fullName}
                </span>
              ) : (
                format(
                  new Date(activity.shiftPosition.date),
                  DATE_FORMATS.DATE_FORMAT_SHORT
                )
              )}
            </td>
            {activity.shiftPosition.jobAssignment?.job &&
              getStatusColumn(
                activity.shiftPosition.jobAssignment.job,
                activity,
                noticedMissingHoursRefreshQueryData
              )}
            <td>
              {!activity.shiftPosition.jobAssignment?.onBoardingCompleted ? (
                <Button
                  size="sm"
                  variant="light"
                  className="row-at-button"
                  onClick={() =>
                    completeOnboardingAction([
                      activity.shiftPosition.jobAssignment!,
                    ])
                  }
                >
                  MARK COMPLETED
                </Button>
              ) : (
                <FcCheckmark
                  color="var(--black)"
                  className="cursor-point"
                  onClick={() =>
                    completeOnboardingAction(
                      [activity.shiftPosition.jobAssignment!],
                      false
                    )
                  }
                />
              )}
            </td>
            <td>
              {getFormattedShiftTimes(activity.shiftPosition, job?.timezone)}
            </td>
            <td>
              {getCheckInValue(
                job,
                activity,
                handleOpenReviewShiftModal,
                noticedMissingActivitiesRefreshQueryData
              )}
            </td>
            <td>
              {getCheckOutValue(
                job,
                activity,
                handleOpenReviewShiftModal,
                noticedMissingActivitiesRefreshQueryData
              )}
            </td>
            <td>
              {(activity.timeSheet?.localizedStart &&
                activity.timeSheet?.localizedEnd && (
                  <div>
                    {`${format(
                      new Date(activity.timeSheet.localizedStart),
                      DATE_FORMATS.TIME_FORMAT
                    )} - ${format(
                      new Date(activity.timeSheet.localizedEnd),
                      DATE_FORMATS.TIME_FORMAT
                    )}`}
                  </div>
                )) ||
                '-'}
            </td>
            <td>{activity.timeSheet?.hoursWorked || '-'}</td>
            <td>
              {activity.shiftPosition?.wage && (
                <div>
                  {'$ '}
                  {Number(activity.shiftPosition.wage.payRate)}/
                  {activity.shiftPosition.wage.payRatePeriod.toLowerCase()}
                </div>
              )}
            </td>
            <td>
              {(activity.timeSheet?.workerEarnedMoney && (
                <div>
                  {'$ '}
                  {Number(activity.timeSheet?.workerEarnedMoney).toFixed(2)}
                </div>
              )) ||
                '-'}
            </td>
            <td>
              {activity.shiftPosition.isPaid ? (
                <FcCheckmark
                  className="cursor-point"
                  onClick={() => {
                    handleConfirmMarkUnPaid(
                      groupedBy,
                      activity.shiftPosition.id,
                      activities,
                      true
                    );
                  }}
                />
              ) : (
                <Button
                  size="sm"
                  variant="light"
                  className="row-at-button"
                  disabled={!isShiftPayable(job, activity)}
                  onClick={() =>
                    handleConfirmMarkPaid(
                      groupedBy,
                      activity.shiftPosition.id,
                      activities
                    )
                  }
                >
                  MARK PAID
                </Button>
              )}
            </td>
            <td className="text-end">
              <Stack direction="horizontal" gap={2} className="text-end">
                {viewType === ActivityTrackerView.DATE && (
                  <TbMessageCircle2
                    color="var(--black)"
                    size={24}
                    className="cursor-point"
                    onClick={() => {
                      if (activity.shiftPosition.jobAssignment) {
                        openChat(
                          activity.shiftPosition.jobAssignment.worker,
                          activity.shiftPosition.jobAssignment.id,
                          shouldShowWorkerInfoInChat({
                            status: activity.shiftPosition.jobAssignment.status,
                          })
                        );
                      }
                    }}
                  />
                )}
                {!activity.timeSheet &&
                  hasShiftStarted(activity.shiftPosition) && (
                    <FaRegClock
                      color="var(--black)"
                      size={20}
                      className="cursor-point"
                      onClick={() => handleOpenSubmitHoursModal(activity)}
                    />
                  )}
                <BiEdit
                  color="var(--black)"
                  size={24}
                  className="cursor-point"
                  onClick={() => handleOpenReviewShiftModal(activity)}
                  aria-label={`Review ${activity.shiftPosition.jobAssignment?.worker.fullName}`}
                />
              </Stack>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
